import { useCallback, useState } from 'react'
import deburr from 'lodash/deburr'

import api from '@/lib/api'
import { useGeneratePresignedUrlMutation, GetMyInfosDocument, useUploadFileMutation, GetOrderDocument } from '@/core/graphql/hooks'
import { ObjectType } from '@/core/api/modules/document'

export function useUploadFile() {
  const [isUploadingFile, setIsUploadingFile] = useState(false)

  const [generatePresignedUrl] = useGeneratePresignedUrlMutation()

  const [uploadFileMutation] = useUploadFileMutation({
    refetchQueries: [{ query: GetMyInfosDocument }, { query: GetOrderDocument }],
    awaitRefetchQueries: true,
  })

  const uploadFile = useCallback(
    async (file: File, objectId: string, objectType: ObjectType = 'patient') => {
      try {
        setIsUploadingFile(true)
        const { data } = await generatePresignedUrl({
          variables: {
            payload: {
              filename: file.name,
              mimetype: file.type,
              objectType,
              objectId,
            },
          },
        })

        const presignedUrl = data?.generatePresignedUrl
        if (!presignedUrl) return

        await api.document.uploadDocumentToAwsS3(presignedUrl, file)
        const key = `${objectType}/${objectId}/${deburr(file.name)}`

        await uploadFileMutation({
          variables: {
            payload: {
              filename: file.name,
              key,
              objectType,
              objectId,
            },
          },
        })
      } catch (error) {
        throw error
      } finally {
        setIsUploadingFile(false)
      }
    },
    [generatePresignedUrl, uploadFileMutation]
  )

  return [uploadFile, isUploadingFile] as const
}
